<template>
    <div>
        <q-banner
            v-if="showReloadBannerFlag"
            class="text-white bg-red"
            inline-actions
        >
            {{
                $t(
                    'Failed to load facilities. Press the reload button to try again.'
                )
            }}
            <template v-slot:action>
                <q-btn
                    @click="loadFacilities"
                    :label="$t('Reload facilities')"
                    color="white"
                    text-color="red"
                />
            </template>
        </q-banner>

        <div
            v-if="facilities && facilities.length > 0"
            class="row q-col-gutter-md"
        >
            <div class="col-12">
                <div class="row justify-end">
                    <div>
                        <q-btn
                            id="report-button"
                            v-if="
                                $userCan('view', 'reports') &&
                                isLiveOEEModuleEnabled
                            "
                            @click="
                                $router.push({
                                    name: 'reports_select'
                                })
                            "
                            :label="$q.screen.gt.sm ? $t('Reports') : ''"
                            class="q-mr-sm bg-white"
                            text-color="accent"
                            outline
                            icon="fas fa-chart-area"
                            color="white"
                        />
                        <q-btn
                            @click="
                                $router.push({
                                    name: 'settings'
                                })
                            "
                            :label="$q.screen.gt.sm ? $t('Settings') : ''"
                            class="bg-white"
                            text-color="accent"
                            outline
                            icon="fas fa-cogs"
                        />
                    </div>
                </div>
            </div>
            <div
                class="col-lg-3 col-md-4 col-sm-6 col-xs-12"
                v-for="(facility, index) in facilities"
                :key="index"
            >
                <facility-card
                    @click="select(facility)"
                    :name="facility.name"
                    :city="facility.city"
                    :country="facility.country"
                    :eventsCount="Math.floor(Math.random() * 10)"
                >
                </facility-card>
            </div>
        </div>
        <!-- Request to load facilities succeeded, but none was found -->
        <div
            v-if="
                facilities && facilities.length === 0 && !showReloadBannerFlag
            "
        >
            <!-- User has no access to any facility -->
            <div v-if="isSuperuser" class="row">
                <div class="col">
                    {{
                        $t(
                            'No facilities found. This could be due to limited access or facilities not yet initialized. Go to the settings and log in to the Admin Portal to set access or create facilities.'
                        )
                    }}
                </div>
                <q-btn
                    @click="
                        $router.push({
                            name: 'settings'
                        })
                    "
                    :label="$t('Settings')"
                    color="secondary"
                    icon="fas fa-cogs"
                ></q-btn>
            </div>
            <!-- Customer account has no facilities -->
            <div v-else>
                {{
                    $t(
                        'No facilities found. This could be due to limited access or facilities not yet initialized. Contact your account admin.'
                    )
                }}
            </div>
        </div>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import FacilityCard from '@components/FacilityCard.vue';
import { useSharedStore } from '@stores/shared.js';
import { mapActions, mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useFacilitiesStore } from '@stores/facilities.ts';
import { useSettingsStore } from '@stores/settings';
import { determineFacilityRoute } from '@utils/facilityNavigation';

export default {
    name: 'FacilityList',
    components: { FacilityCard },
    mixins: [BreadcrumbsManager],
    data() {
        return {
            isLoading: false
        };
    },
    async mounted() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities')
            }
        ]);
        this.selectFacilityAction(null);
    },
    computed: {
        ...mapState(useAuthStore, ['user', 'isSuperuser', 'getUserRole']),
        ...mapState(useFacilitiesStore, ['getFacilities']),
        ...mapState(useSettingsStore, [
            'isLiveOEEModuleEnabled',
            'isPlanningModuleEnabled'
        ]),
        /**
         * Get facilities.
         * @return {Object[]} - Array of facilities.
         */
        facilities() {
            return this.getFacilities;
        },
        /**
         * Get showReloadBannerFlag from query parameter.
         * The flag is set to true when the request to load facilities fails.
         * See loadFacilitiesIfNeeded method in boot/auth.js.
         * @return {Boolean} - True if showReloadBannerFlag is true.
         */
        showReloadBannerFlag() {
            return this.$route.query.showReloadBanner === 'true';
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useFacilitiesStore, [
            'loadFacilitiesAction',
            'selectFacilityAction'
        ]),
        /**
         * Load facilities into store.
         */
        async loadFacilities() {
            // begin loading
            this.beginLoading(this.$t('Loading facilities'));
            try {
                await this.loadFacilitiesAction();
                // set showReloadBanner meta parameter to false
                this.$router.replace({
                    query: {}
                });
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to load facilities. Please try again later.',
                        error
                    )
                );
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Handles facility selection.
         * @param facility
         */
        select(facility) {
            this.selectFacilityAction(facility.id);

            const resultingRoute = determineFacilityRoute(
                this.getUserRole,
                this.isLiveOEEModuleEnabled,
                this.isPlanningModuleEnabled,
                facility.id
            );

            // Handle error case - typically when user role requires a module that isn't enabled
            if (resultingRoute.error) {
                this.$notifyError(
                    this.$t(
                        'Your account has a planner role but the planning module is not enabled. Please contact support.'
                    )
                );
                return;
            }

            // Navigate to the determined route if available
            if (resultingRoute.name) {
                this.$router.push({
                    name: resultingRoute.name,
                    params: resultingRoute.params
                });
            }
        }
    }
};
</script>
